// src/features/chat/elora-chat/EloraChat.tsx
import * as React from 'react';
import { createChatStore, type ChatStore, type Id } from '@norkin/chat-core';
import { ChatProvider, Conversation, useThread } from '@norkin/chat-react';
import { runOpenAIChatWithTools, type ToolDefinition } from '@norkin/chat-openai';
import { ChevronDown } from 'lucide-react';
import env from '@shared/api/env';
import { getSupabaseAuthHeaders } from '@shared/api/supabase-auth-headers';
import { dispatchEloraCommand, extractCommandEnvelopes } from '@/services/elora-tools';
import { ChatMessageList } from '@features/chat/components/shadcn-chat/ChatMessageList';
import { ChatComposer } from '@features/chat/components/shadcn-chat/ChatComposer';
import { Button } from '@/components/ui/button';
import { cn } from '@shared/utils/cn';

export type EloraChatProps = {
  flowId: string;
  workspaceId?: string | null;
  onRunnerReady?: (runner: { isRunning: boolean; reset(): void }) => void;
};

const ELORA_MODEL = 'claude-sonnet-4-20250514';

const ELORA_SYSTEM_PROMPT = `You are Elora, an assistant that helps users edit their workflow canvas.
Always prefer the provided tools (searchNode, addNode, removeNode, addEdge, removeEdge, editNodeAttributes)
instead of describing actions. Reply concisely and summarize any changes after executing tools.`;

const ELORA_TOOLS: ToolDefinition[] = [
  {
    type: 'function',
    function: {
      name: 'searchNode',
      description: 'Search available node templates by keyword.',
      parameters: {
        type: 'object',
        properties: {
          query: { type: 'string', description: 'Search text, e.g. "chat" or "log".' },
          topK: { type: 'integer', minimum: 1, maximum: 20, description: 'Maximum results to return (default 5).' },
        },
        required: ['query'],
      },
    },
  },
  { type: 'function', function: { name: 'addNode', description: 'Insert a node into the canvas.', parameters: { type: 'object' } } },
  { type: 'function', function: { name: 'removeNode', description: 'Remove a node from the canvas.', parameters: { type: 'object' } } },
  { type: 'function', function: { name: 'addEdge', description: 'Connect two nodes with an edge.', parameters: { type: 'object' } } },
{ type: 'function', function: { name: 'removeEdge', description: 'Remove an edge between two nodes.', parameters: { type: 'object' } } },
{ type: 'function', function: { name: 'editNodeAttributes', description: 'Patch attributes on an existing node.', parameters: { type: 'object' } } },
];

function Viewport(props: any & { isStreaming: boolean }) {
  const { threadId, messages, containerProps, bottomAnchorProps, atBottom, hasScrollableContent, scrollToBottom, usesWindowScroll, isStreaming } = props;
  const [anchorVisible, setAnchorVisible] = React.useState(true);

  React.useEffect(() => {
    const anchor = bottomAnchorProps.ref.current;
    const container = containerProps.ref.current;
    if (!hasScrollableContent || !anchor) {
      setAnchorVisible(true);
      return;
    }
    if (typeof IntersectionObserver !== 'function') {
      setAnchorVisible(atBottom);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => setAnchorVisible(entries.some((entry) => entry.isIntersecting)),
      usesWindowScroll || !container ? undefined : { root: container },
    );
    observer.observe(anchor);
    return () => observer.disconnect();
  }, [bottomAnchorProps, containerProps, hasScrollableContent, atBottom, usesWindowScroll]);

  const showScrollButton = hasScrollableContent && !anchorVisible;

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        {...containerProps}
        className={cn('flex-1 min-h-0 overflow-y-auto overscroll-contain rounded-lg bg-card pb-28', containerProps.className)}
        role="log"
        aria-label="Elora chat transcript"
        aria-live="polite"
      >
        <ChatMessageList threadId={threadId} messages={messages} isStreaming={isStreaming} bottomAnchorProps={bottomAnchorProps} />
      </div>
      {showScrollButton ? (
        <Button
          type="button"
          size="icon"
          variant="secondary"
          className="absolute bottom-4 right-4 h-9 w-9 rounded-full shadow-md"
          aria-label="Scroll to latest"
          onClick={scrollToBottom}
        >
          <ChevronDown className="h-4 w-4" />
        </Button>
      ) : null}
    </div>
  );
}

type EloraChatInnerProps = {
  flowId: string;
  workspaceId?: string | null;
  threadId: Id;
  store: ChatStore;
  onRunnerReady?: (runner: { isRunning: boolean; reset(): void }) => void;
};

function EloraChatInner({ flowId, workspaceId, threadId, store, onRunnerReady }: EloraChatInnerProps) {
  useThread(threadId);

  const [isRunning, setIsRunning] = React.useState(false);
  const abortRef = React.useRef<AbortController | null>(null);

  const handleSend = React.useCallback(
    async (assistantMessageId: Id) => {
      if (!assistantMessageId) return;
      const base = (env.CONDUIT_BASE || env.BLAST_API || '').replace(/\/$/, '');
      if (!base) throw new Error('CONDUIT_BASE (or BLAST_API) is not configured.');
      const encodedFlowId = encodeURIComponent(flowId);
      const url = new URL(`/elora/chat/${encodedFlowId}`, base);
      if (workspaceId) url.searchParams.set('workspaceId', workspaceId);

      const authHeaders = await getSupabaseAuthHeaders();
      const controller = new AbortController();
      abortRef.current = controller;
      setIsRunning(true);

      try {
        await runOpenAIChatWithTools({
          store,
          threadId,
          assistantMessageId,
          model: ELORA_MODEL,
          system: ELORA_SYSTEM_PROMPT,
          chatCompletionsURL: url.toString(),
          headers: {
            ...authHeaders,
            ...(workspaceId ? { 'X-Workspace-ID': workspaceId } : {}),
            Accept: 'text/event-stream',
          },
          tools: ELORA_TOOLS,
          handleToolsLocally: false,
          // Consume backend SSE events and forward envelopes to the canvas dispatcher.
          onEvent(event) {
            try {
              const rawType = (event as { type?: string; event?: string } | null)?.type ?? (event as any)?.event;
              if (import.meta.env?.DEV) {
                console.debug('[Elora] chat-openai event', rawType, event);
              }
              const isCompleted = rawType === 'tool-result:completed' || rawType === 'tool-result';
              if (!isCompleted) {
                return;
              }
              let payload: unknown;
              const content = (event as any)?.content;
              if (content && typeof content === 'object') {
                const { format } = content as { format?: string };
                if (format === 'json') {
                  payload = (content as any).json;
                } else if ((format === 'text' || format === 'markdown') && typeof (content as any).text === 'string') {
                  payload = (content as any).text;
                }
              }
              if (payload === undefined) {
                payload =
                  (event as any)?.payload ??
                  (event as any)?.result ??
                  (event as any)?.data ??
                  (event as any)?.json ??
                  (event as any)?.content?.json ??
                  (event as any)?.content?.text ??
                  (event as any)?.error ??
                  null;
              }
              const envelopes = extractCommandEnvelopes(payload);
              if (envelopes.length) {
                for (const envelope of envelopes) {
                  void Promise.resolve(dispatchEloraCommand(envelope)).catch((dispatchError) => {
                    console.warn('[Elora] command dispatch failed', dispatchError, envelope);
                  });
                }
              }
            } catch (err) {
              // Non-fatal: UI should never crash on stream noise
              console.warn('[Elora] onEvent(tool-result) parse/dispatch failed', err);
            }
          },
          signal: controller.signal,
        });
      } catch (err) {
        console.error('[Elora] streaming failed', err);
      } finally {
        setIsRunning(false);
        abortRef.current = null;
      }
    },
    [flowId, store, threadId, workspaceId],
  );

  const handleReset = React.useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsRunning(false);
  }, []);

  React.useEffect(() => {
    onRunnerReady?.({ isRunning, reset: handleReset });
  }, [handleReset, isRunning, onRunnerReady]);

  return (
    <div className="relative flex h-full w-full min-h-[420px] flex-col gap-4">
      <div className="flex flex-1 min-h-0 w-full flex-col">
        <Conversation threadId={threadId} bottomThreshold={72}>
          {(ctx) => <Viewport {...ctx} isStreaming={isRunning} />}
        </Conversation>
      </div>
      <div className="sticky bottom-0 z-10 w-full">
        <ChatComposer
          threadId={threadId}
          disabled={isRunning}
          onSend={handleSend}
          className="border-border/80 bg-card/80 backdrop-blur supports-[backdrop-filter]:bg-card/60"
        />
      </div>
    </div>
  );
}

export function EloraChat({ flowId, workspaceId, onRunnerReady }: EloraChatProps) {
  const [chatState, setChatState] = React.useState(() => ({ key: 0, store: createChatStore({ microBatchMs: 0 }) }));

  React.useEffect(() => () => chatState.store.dispose(), [chatState.store]);

  const resetConversation = React.useCallback(() => {
    setChatState((prev) => {
      return { key: prev.key + 1, store: createChatStore({ microBatchMs: 0 }) };
    });
  }, []);

  const initialRef = React.useRef(true);
  React.useEffect(() => {
    if (initialRef.current) {
      initialRef.current = false;
      return;
    }
    resetConversation();
  }, [flowId, resetConversation]);

  const threadId = React.useMemo<Id>(() => `elora-${flowId}` as Id, [flowId]);

  const handleInnerRunnerReady = React.useCallback(
    (runner: { isRunning: boolean; reset(): void }) => {
      if (!onRunnerReady) return;
      onRunnerReady({
        isRunning: runner.isRunning,
        reset: () => {
          runner.reset();
          resetConversation();
        },
      });
    },
    [onRunnerReady, resetConversation],
  );

  return (
    <ChatProvider key={chatState.key} store={chatState.store}>
      <EloraChatInner
        flowId={flowId}
        workspaceId={workspaceId}
        threadId={threadId}
        store={chatState.store}
        onRunnerReady={handleInnerRunnerReady}
      />
    </ChatProvider>
  );
}

export default EloraChat;
